import { DestinationFeature, parseDestinationId } from '@mpr/shared';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { api, ApiError } from '../lib/api.js';
import type { Selection } from '../App.js';
import { useI18n } from '../i18n/LanguageProvider.js';
import { DestinationChecklist } from './DestinationPicker.js';
import { Icon } from './Icons.js';

/**
 * Targets and Publish.
 *
 * Pages without CREATE_CONTENT are shown but not selectable — greying them out
 * with the reason is more useful than hiding them, because "why is my Page
 * missing?" is the first question an operator asks.
 */
export function PublishPanel({
  selection,
  onPublished,
  onStartOver,
}: {
  selection: Selection | null;
  /** Told with the id of the job that was queued, so a caller can link it to what it came from. */
  onPublished: (jobId: string) => void;
  /** Clear the chosen content and reopen the picker. */
  onStartOver?: () => void;
}) {
  // Destination ids (`page:…` / `soc:…`): one list for every network.
  const [destinations, setDestinations] = useState<string[]>([]);
  const parsed = destinations
    .map((id) => parseDestinationId(id))
    .filter((entry): entry is NonNullable<typeof entry> => entry !== null);
  const targets = parsed.filter((entry) => entry.kind === 'page').map((entry) => entry.id);
  const socialTargets = parsed.filter((entry) => entry.kind === 'soc').map((entry) => entry.id);
  const instagramTargets = parsed.filter((entry) => entry.kind === 'ig').map((entry) => entry.id);
  const [allowRepublish, setAllowRepublish] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  /*
   * A local wall-clock string from <input type="datetime-local">, converted to
   * an absolute instant on submit. "Nine on Thursday" means nine where the
   * operator is, and the browser is the only party that knows where that is —
   * resolving it here beats storing a zone name the server reinterprets months
   * later, possibly across a daylight-saving change.
   */
  const [scheduleAt, setScheduleAt] = useState('');
  const { t } = useI18n();


  const publish = useMutation({
    mutationFn: () =>
      api.createJob({
        sourceKind: selection?.sourceKind,
        sourcePostId: selection?.sourcePostId,
        compositionId: selection?.compositionId,
        targetPageIds: targets,
        socialAccountIds: socialTargets,
        instagramPageIds: instagramTargets,
        alsoAs: selection?.alsoAs,
        allowRepublish,
        scheduledAt: scheduleAt ? new Date(scheduleAt).toISOString() : undefined,
      }),
    onSuccess: (data) => {
      setResult(
        (data.scheduledAt
          ? t('publish.scheduled', {
              count: data.queuedTargets,
              when: new Date(data.scheduledAt).toLocaleString(),
            })
          : t('publish.queued', { count: data.queuedTargets })) +
          (data.skipped.length > 0
            ? ' ' +
              t('publish.skipped', {
                count: data.skipped.length,
                reasons: data.skipped.map((s) => s.reason).join('; '),
              })
            : ''),
      );
      /*
       * The chosen Pages are cleared, the chosen content is not.
       *
       * Publishing the same thing to another Page is a real second step, and
       * wiping everything would make it a re-hunt. Leaving the ticks in place
       * was worse: the form looked exactly as it had a moment earlier, so there
       * was nothing to say the work was done, and the only way out was Cancel.
       */
      setDestinations([]);
      setScheduleAt('');
      onPublished(data.jobId);
    },
  });

  if (!selection) {
    return (
      <section className="panel">
        <h2>{t('tab.publish')}</h2>
        <div className="empty-state">
          <Icon.send />
          <p>{t('publish.nothingSelected')}</p>
        </div>

        {/* The places it can go, shown before there is anything to send: a
            screen that stays blank until step one is done hides what step two
            even offers. */}
        <h2 style={{ marginTop: 26 }}>{t('publish.targetsTitle')}</h2>
        <p className="muted small">{t('publish.destinationsLocked')}</p>
        <DestinationChecklist
          feature={DestinationFeature.PUBLISH}
          selected={[]}
          onChange={() => undefined}
          locked
        />
      </section>
    );
  }

  const chosenCount = destinations.length;

  return (
    <section className="panel">
      <h2>{t('publish.postTitle')}</h2>
      <div className="card">
        <dl className="facts">
          <dt>{t('publish.source')}</dt>
          <dd>{selection.sourceKind}</dd>
          <dt>{t('publish.reference')}</dt>
          <dd className="mono small">{selection.label}</dd>
          <dt>{t('publish.preview')}</dt>
          <dd>{selection.preview}</dd>
        </dl>
      </div>

      <h2 style={{ marginTop: 26 }}>{t('publish.targetsTitle')}</h2>
      <p className="muted small">{t('publish.targetsHint')}</p>

      <DestinationChecklist
        feature={DestinationFeature.PUBLISH}
        selected={destinations}
        onChange={setDestinations}
      />

      {/*
        Empty means now, which is what the button has always done. An operator
        who never touches this field sees no change in behaviour — the field is
        an addition, not a step added to the flow.
      */}
      <div className="card" style={{ marginTop: 14 }}>
        <label htmlFor="schedule-at">{t('publish.scheduleLabel')}</label>
        <input
          id="schedule-at"
          type="datetime-local"
          value={scheduleAt}
          onChange={(event) => setScheduleAt(event.target.value)}
        />
        <p className="tiny muted" style={{ margin: '6px 0 0' }}>
          {scheduleAt
            ? t('publish.scheduleSet', { when: new Date(scheduleAt).toLocaleString() })
            : t('publish.scheduleHint')}
        </p>
      </div>

      <label className="checkbox-row">
        <input
          type="checkbox"
          checked={allowRepublish}
          onChange={(event) => setAllowRepublish(event.target.checked)}
        />
        <span>
          {t('publish.allowRepublish')}{' '}
          <span className="muted small">{t('publish.allowRepublishHint')}</span>
        </span>
      </label>

      {publish.isError && (
        <div className="notice error">
          {publish.error instanceof ApiError ? publish.error.message : t('publish.failed')}
        </div>
      )}
      {result && (
        <div className="notice ok published-done">
          <p style={{ margin: 0 }}>{result}</p>
          <div className="published-next">
            {/* Both intentions, named. Neither is the quiet default. */}
            <button
              type="button"
              className="ghost small"
              onClick={() => {
                setResult(null);
                setAllowRepublish(true);
              }}
            >
              {t('publish.sameToOthers')}
            </button>
            {onStartOver && (
              <button
                type="button"
                className="ghost small"
                onClick={() => {
                  setResult(null);
                  setAllowRepublish(false);
                  onStartOver();
                }}
              >
                {t('publish.somethingElse')}
              </button>
            )}
          </div>
        </div>
      )}

      <button
        type="button"
        className="primary"
        onClick={() => publish.mutate()}
        disabled={chosenCount === 0 || publish.isPending}
      >
        <Icon.send />
        {publish.isPending ? t('publish.queueing') : t('publish.button', { count: chosenCount })}
      </button>
    </section>
  );
}
